// Default FAQ sets for every prediction hub (league / market / team / club /
// competition / country). Google requires FAQPage nodes to mirror content that
// is visible on the page, so these entries are rendered on-page by ClusterHub
// and emitted as FAQPage JSON-LD from the exact same source.
//
// Copy is deliberately compliance-safe: no guaranteed-win language, no implied
// certainty — only how the model works and how to read the output.

import type { ClusterKind } from "@/hooks/useClusterPredictions";

export interface ClusterFaq {
  question: string;
  answer: string;
}

/** Human-readable noun for the hub type, used inside the answers. */
const KIND_NOUN: Record<string, string> = {
  league: "league",
  market: "market",
  team: "team",
  club: "club",
  competition: "competition",
  country: "country",
};

/**
 * Build a four-entry FAQ set for a hub. Deterministic (no randomness) so the
 * prerendered HTML, the runtime render and the CI validator always agree.
 */
export function clusterFaqs(opts: {
  kind: ClusterKind | string;
  /** Display heading, e.g. "Champions League Predictions". */
  heading: string;
}): ClusterFaq[] {
  const noun = KIND_NOUN[String(opts.kind)] ?? "section";
  // Strip a trailing "Predictions" so the label reads naturally in a sentence.
  const label = opts.heading.replace(/\s+predictions?$/i, "").trim() || opts.heading;

  return [
    {
      question: `How are ${label} predictions generated?`,
      answer:
        `Every ${label} pick is produced by Statlign's multi-strategy engine, which scores recent form, ` +
        `expected goals, head-to-head history, squad availability and live bookmaker odds. A prediction is ` +
        `only published when several independent strategies agree, and each one ships with the reasoning ` +
        `behind it so you can judge it yourself.`,
    },
    {
      question: `How often are ${label} predictions updated?`,
      answer:
        `This ${noun} page refreshes continuously. Odds and fixtures are re-ingested through the day, ` +
        `AI-verified kickoff times are re-checked before publication, and picks are re-scored whenever new ` +
        `data (line-ups, injuries, market movement) arrives — so the newest fixtures always appear first.`,
    },
    {
      question: `What does the confidence score on a ${label} prediction mean?`,
      answer:
        `Confidence reflects how many validated strategies agreed and how strong the supporting data was, ` +
        `on a 55–82 scale. It is a measure of analytical agreement, not a promise of an outcome: higher ` +
        `confidence simply means more of our models converged on the same selection.`,
    },
    {
      question: `Are ${label} predictions free to view?`,
      answer:
        `Yes — historical results and a sample of upcoming ${label} picks are free. Revealing a full pick ` +
        `costs 2 credits and unlocking its strategy insights costs 1 credit. Betting always carries risk, so ` +
        `treat every prediction as research and never stake more than you can afford to lose.`,
    },
  ];
}

/**
 * FAQPage node for a hub, built from the same entries rendered on-page.
 * Shared by ClusterHub and `scripts/verify-cluster-faq-jsonld.ts` so the
 * validator checks the exact markup the page emits.
 */
export function clusterFaqJsonLd(faqs: ClusterFaq[]): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  };
}

/**
 * `SportsOrganization` node describing the governing league of a league hub, so
 * every league-filtered section carries league schema (not just one hub).
 */
export function clusterLeagueJsonLd(opts: {
  heading: string;
  canonicalPath: string;
  siteUrl: string;
}): Record<string, unknown> {
  const url = `${opts.siteUrl}${opts.canonicalPath}`;
  return {
    "@context": "https://schema.org",
    "@type": "SportsOrganization",
    "@id": `${url}#league`,
    name: opts.heading.replace(/\s+predictions?$/i, "").trim() || opts.heading,
    alternateName: opts.heading,
    sport: "Association football",
    url,
    subjectOf: { "@id": `${url}#webpage` },
  };
}

/**
 * Merge curated FAQ entries with the shared defaults so every hub / landing
 * reaches Google's minimum of two Q&A pairs, without duplicating a question the
 * editorial copy already answers.
 */
export function mergeClusterFaqs(
  curated: ClusterFaq[] | undefined,
  opts: { kind: ClusterKind | string; heading: string; min?: number },
): ClusterFaq[] {
  const min = opts.min ?? 2;
  const out = [...(curated ?? [])];
  const seen = new Set(out.map((f) => f.question.trim().toLowerCase()));
  for (const f of clusterFaqs(opts)) {
    if (out.length >= min) break;
    const key = f.question.trim().toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(f);
  }
  return out;
}
